import React from 'react';
import { Box, Typography, Avatar, alpha, useTheme, Grid, Chip, Divider } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { StyledCard, StyledCardHeader, StyledCardContent, MetricChip, formatDisplayItem } from '@features/analysis/components/StyledComponents';
import { AIAnalysisResult } from '@features/analysis/types';

interface FinancialAnalysisCardProps {
  analysis: AIAnalysisResult;
}

const FinancialAnalysisCard: React.FC<FinancialAnalysisCardProps> = ({ analysis }) => {
  const theme = useTheme();
  const financialAnalysis = analysis.financialAnalysis;

  // Helper function to format metric values
  const formatMetricValue = (value: number | string, isPercentage?: boolean) => {
    if (typeof value === 'string') return value;
    if (isPercentage) return `${value}%`;
    return typeof value === 'number' ? `$${value.toLocaleString()}` : value;
  };

  return (
    <StyledCard>
      <StyledCardHeader
        title="Financial Analysis"
        subheader="Revenue performance, costs, and profitability insights"
        avatar={
          <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
            <AttachMoneyIcon />
          </Avatar>
        }
      />
      <StyledCardContent>
        {/* Key Metrics Calculation */}
        {financialAnalysis?.keyMetricCalculation && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 2 }}>
              Key Financial Metrics
            </Typography>
            <Grid container spacing={2}>
              {financialAnalysis.keyMetricCalculation.ADR_Estimate && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Average Daily Rate (ADR)
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main, mb: 0.5 }}>
                      ${financialAnalysis.keyMetricCalculation.ADR_Estimate}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {financialAnalysis.keyMetricCalculation.Occupancy_Estimate && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Occupancy Rate
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main, mb: 0.5 }}>
                      {financialAnalysis.keyMetricCalculation.Occupancy_Estimate}%
                    </Typography>
                  </Box>
                </Grid>
              )}
              {financialAnalysis.keyMetricCalculation.RevPAR_Estimate && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Revenue Per Available Room (RevPAR)
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main, mb: 0.5 }}>
                      ${financialAnalysis.keyMetricCalculation.RevPAR_Estimate}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Analysis Sections */}
        <Grid container spacing={3}>
          {/* Revenue Analysis */}
          {financialAnalysis?.revenueAnalysis && (
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  Revenue Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {financialAnalysis.revenueAnalysis}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Expense Analysis */}
          {financialAnalysis?.expenseAnalysis && (
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  Expense Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {financialAnalysis.expenseAnalysis}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Profitability Assessment */}
          {financialAnalysis?.profitabilityAssessment && (
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  Profitability Assessment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {financialAnalysis.profitabilityAssessment}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Projection Feasibility */}
          {financialAnalysis?.projectionFeasibility && (
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  Projection Feasibility
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {financialAnalysis.projectionFeasibility}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Financial Risks */}
        {financialAnalysis?.financialRisks && financialAnalysis.financialRisks.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 2 }}>
                Financial Risks
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {financialAnalysis.financialRisks.map((risk, index) => (
                  <MetricChip
                    key={index}
                    label={formatDisplayItem(risk)}
                    color="warning"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          </>
        )}

        {/* Empty State */}
        {!financialAnalysis && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No financial analysis available. Include financial documents in your analysis configuration.
            </Typography>
          </Box>
        )}
      </StyledCardContent>
    </StyledCard>
  );
};

export default FinancialAnalysisCard;